import { computed, ref, watch } from 'vue';
import { connectTileId, flushAccountConfigs } from '@blpde/connect';
import { useTileWorkspaces } from '@/tiles/useTileWorkspaces';
import {
  fetchSessionScopes,
  switchSessionScope,
  type SessionScope,
  type SessionScopeCompany,
  type SessionScopes,
  type SessionScopeTenant,
} from '@/auth';

/**
 * The session's tenant/company scope for the shell: what the account may
 * switch to (GET /v1/session/scopes) and the switch itself.
 *
 * The scope lives on the master session, not in the tab: every token of the
 * session and every tab follow a switch. After it the shell reloads — that
 * drops the shared socket (bound to the old scope at its handshake), every
 * cache that is not keyed by company, and the open views in one go — and tells
 * the other tabs to do the same.
 */

const CHANNEL = 'formica.session-scope';

const scopes = ref<SessionScopes | null>(null);
const loading = ref(false);
const switching = ref(false);
const switchError = ref<string | null>(null);

let channel: BroadcastChannel | null = null;

function openChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try {
    channel = new BroadcastChannel(CHANNEL);
  } catch {
    channel = null;
  }
  return channel;
}

function reloadShell(): void {
  window.location.reload();
}

/**
 * Reload this tab when another tab of the session switched the scope: its
 * data belongs to the old company now. Open Write and Calc documents are kept
 * on the device and come back after the reload (I-3006). A tile leaves that to
 * the page around it, whose reload takes every tile along.
 */
export function listenForScopeSwitches(): () => void {
  if (connectTileId()) return () => {};
  const ch = openChannel();
  if (!ch) return () => {};
  const onMessage = () => reloadShell();
  ch.addEventListener('message', onMessage);
  return () => ch.removeEventListener('message', onMessage);
}

async function load(): Promise<void> {
  if (loading.value) return;
  loading.value = true;
  try {
    const next = await fetchSessionScopes();
    if (next) scopes.value = next;
  } finally {
    loading.value = false;
  }
}

/** Resolves once no read of the scopes is on its way. */
function scopesSettled(): Promise<void> {
  if (!loading.value) return Promise.resolve();
  return new Promise((resolve) => {
    const stop = watch(loading, (busy) => {
      if (busy) return;
      stop();
      resolve();
    });
  });
}

/**
 * The company the session works in, for what is scoped to it (the
 * notifications of that company, an action that belongs to one). Waits for
 * the scopes when they are not read yet — also for a read another caller
 * started; `fresh` asks master again (the session may have moved since).
 * 0: the session has no company. null: master does not say (signed out,
 * unreachable) — a caller then holds back rather than act unscoped.
 */
export async function sessionCompanyID(fresh = false): Promise<number | null> {
  await scopesSettled();
  if (fresh || !scopes.value) {
    await load();
    await scopesSettled();
  }
  return scopes.value ? scopes.value.companyID : null;
}

/**
 * Opens a hosted app's destination — a shell route path (`route.fullPath`),
 * never a URL. The shell routes by hash, so a path handed to
 * `location.assign` would leave the app; the router turns it into the shell's
 * own href. After a switch the tab reloads into it, so the old scope's socket,
 * caches and views go in one go, as with `reloadShell`.
 */
async function openDestination(destination: string, reload: boolean): Promise<void> {
  // Loaded on demand: the router pulls in every hosted app's entry.
  const { default: router } = await import('@/router');
  if (!reload) {
    await router.push(destination);
    return;
  }
  window.history.pushState(null, '', router.resolve(destination).href);
  reloadShell();
}

/**
 * The switch on the page that holds the tiles: they are all asked, and its reload
 * takes them along. False: a tile kept the user there, nothing happened. Throws
 * what master refuses with.
 */
async function switchPage(
  tenantID: number,
  companyID: number,
  destination?: string
): Promise<boolean> {
  // The reload below drops every tile: they are asked first.
  const tiles = useTileWorkspaces().tileLeaveGuard();
  if (!(await tiles.leave())) return false;
  // Setting changes still waiting for their debounce go out first: the reload
  // below would drop them, and they belong to the scope they were made in.
  // The reloaded shell reads every config key for the new company (I-2510).
  await flushAccountConfigs();
  try {
    await switchSessionScope(tenantID, companyID);
  } catch (err) {
    tiles.stay();
    throw err;
  }
  try {
    openChannel()?.postMessage({ tenantID, companyID });
  } catch {
    // The other tabs catch up on their next reload.
  }
  if (destination) await openDestination(destination, true);
  else reloadShell();
  return true;
}

/** Inside a tile the page around it does the switch, with this tile's changes sent first. */
async function switchFromTile(
  tenantID: number,
  companyID: number,
  destination?: string
): Promise<boolean> {
  const page = pageAroundTile();
  if (!page) return switchPage(tenantID, companyID, destination);
  await flushAccountConfigs();
  try {
    return await page(tenantID, companyID, destination);
  } catch (err) {
    // An error of another window: not an `Error` of this one.
    throw new Error((err as Error).message);
  }
}

/** Switches the session to (tenantID, companyID), then reloads every tab. */
async function switchTo(tenantID: number, companyID: number, destination?: string): Promise<void> {
  if (destination && (!destination.startsWith('/') || destination.startsWith('//')))
    throw new Error('invalid_scope_destination');
  const current = scopes.value;
  if (switching.value) return;
  if (current && current.tenantID === tenantID && current.companyID === companyID) {
    // Same company: a plain navigation, the router's own guards apply.
    if (destination) await openDestination(destination, false);
    return;
  }
  switching.value = true;
  switchError.value = null;
  try {
    if (!(await switchFromTile(tenantID, companyID, destination))) switching.value = false;
  } catch (err) {
    switchError.value = err instanceof Error ? err.message : String(err);
    switching.value = false;
    if (destination) throw err;
  }
}

/**
 * The scopes as master answers them now — another tab may have switched since
 * this one loaded them — else what this tab knows.
 */
async function refreshedScopes(): Promise<SessionScopes | null> {
  const next = await fetchSessionScopes();
  if (next) scopes.value = next;
  return scopes.value;
}

/**
 * Where the session works right now; null while unknown. Hosted apps read it
 * through `readShellScope` (`@blpde/connect`) to note the company a record
 * they link to was read in.
 */
async function currentScope(): Promise<SessionScope | null> {
  const data = await refreshedScopes();
  return data ? { tenantID: data.tenantID, companyID: data.companyID } : null;
}

/**
 * Opens a shell route in a company, named by its master id alone: a plain
 * navigation where the session already works in it, otherwise the switch
 * above — guard, token refresh and the reload of every tab, this one into the
 * route. The tenant is the one that holds the company among the session's
 * scopes; a company that is not among them is refused like a switch master
 * refuses. Hosted apps call it through `openInShellCompany` (`@blpde/connect`).
 */
async function openInCompany(companyID: number, destination: string): Promise<void> {
  const data = await refreshedScopes();
  if (!data) throw new Error('scope_switch_unavailable');
  const tenant = data.tenants.find((item) =>
    item.companies.some((company) => company.id === companyID)
  );
  if (!tenant) throw new Error('company_not_granted');
  await switchTo(tenant.id, companyID, destination);
}

function companyOf(
  data: SessionScopes | null
): { tenant: SessionScopeTenant; company: SessionScopeCompany } | null {
  if (!data) return null;
  for (const tenant of data.tenants) {
    if (tenant.id !== data.tenantID) continue;
    const company = tenant.companies.find((c) => c.id === data.companyID);
    if (company) return { tenant, company };
  }
  return null;
}

/** The i18n key for a refused switch (master's error codes). */
export function scopeSwitchErrorKey(code: string): string {
  switch (code) {
    case 'company_not_granted':
    case 'tenant_not_granted':
      return 'connect.scope.notGranted';
    case 'company_not_active':
    case 'tenant_not_active':
      return 'connect.scope.notActive';
    case 'tenant_without_database':
      return 'connect.scope.withoutDatabase';
    default:
      return 'connect.scope.failed';
  }
}

/** The label a company is shown with: its name, else its code, else its id. */
export function companyLabel(company: SessionScopeCompany): string {
  return company.name.trim() || company.code.trim() || `#${company.id}`;
}

/** The label a tenant is shown with: its key, else its id. */
export function tenantLabel(tenant: SessionScopeTenant): string {
  return tenant.key.trim() || `#${tenant.id}`;
}

/**
 * The name a tenant or company layer of the account config chain is shown
 * with („Geerbt von Firma „BLP““, I-2510): the session's tenant and companies
 * by their labels; undefined for any other layer or one the session does not
 * know.
 */
export function sessionLayerName(layer: { subjectType: string; subjectId: number }): string | undefined {
  const tenants = scopes.value?.tenants ?? [];
  if (layer.subjectType === 'tenant') {
    const tenant = tenants.find((item) => item.id === layer.subjectId);
    return tenant ? tenantLabel(tenant) : undefined;
  }
  if (layer.subjectType === 'company') {
    for (const tenant of tenants) {
      const company = tenant.companies.find((item) => item.id === layer.subjectId);
      if (company) return companyLabel(company);
    }
  }
  return undefined;
}

export function useSessionScope() {
  const active = computed(() => companyOf(scopes.value));
  const tenants = computed(() => scopes.value?.tenants ?? []);
  /** More than one company to pick from — only then is there a switch. */
  const canSwitch = computed(() => tenants.value.reduce((n, t) => n + t.companies.length, 0) > 1);
  return {
    scopes,
    tenants,
    active,
    canSwitch,
    loading,
    switching,
    switchError,
    load,
    switchTo,
  };
}

/** Test seam: forget the loaded scopes and the channel. */
export function resetSessionScope(): void {
  scopes.value = null;
  loading.value = false;
  switching.value = false;
  switchError.value = null;
  channel?.close();
  channel = null;
}

type ScopeWindow = Window & {
  formica?: {
    switchScope?: typeof switchTo;
    switchPage?: typeof switchPage;
    openInCompany?: typeof openInCompany;
    sessionScope?: typeof currentScope;
  };
};

/** The switch of the page around this tile; null outside a tile. */
function pageAroundTile(): typeof switchPage | null {
  if (!connectTileId() || window.parent === window) return null;
  try {
    return (window.parent as ScopeWindow).formica?.switchPage ?? null;
  } catch {
    return null;
  }
}

// Hosted apps use the same guarded switch, token refresh and cross-tab reset
// as the shell picker; they must not switch only a local application store.
if (typeof window !== 'undefined') {
  const host = window as ScopeWindow;
  host.formica = {
    ...host.formica,
    switchScope: switchTo,
    switchPage,
    openInCompany,
    sessionScope: currentScope,
  };
}
